import React, { useCallback, useState } from 'react';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import styled from '@emotion/native';
import { Entypo } from '@expo/vector-icons';
import BackButton from '@/shared/components/BackButton';
import Footer from '@/shared/components/Footer';
import { BodyText, H2 } from '@/shared/components/StyledText';
import {
  MARKETING_FAQ_AUDIENCES,
  MarketingFaqItem,
} from '@/shared/constants/marketingFaqContent';
import {
  accent,
  alphaBlack05,
  darkGreen,
  green,
  lightGrey,
  primaryHover,
  sand,
  subheadline,
  welcomeMutedOnGreen,
} from '@/shared/styles/colors';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import {
  FONT_STYLES,
  WELCOME_SECTION_MAX_WIDTH,
  WELCOME_SECTION_VERTICAL_BASE,
  WELCOME_SECTION_VERTICAL_RHYTHM_EXTRA,
} from '@/shared/styles/constants';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { ROUTES } from '@/shared/constants/routes';
import { applyToBeStylist } from '@/shared/utils';

const SUPPORT_EMAIL = 'support@emmestyling.com';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

function formatAudienceTabDisplay(audienceTitle: string): string {
  return audienceTitle.replace(/:$/, '').trim().toUpperCase();
}

function formatAudienceAccessibilityLabel(audienceTitle: string): string {
  const base = audienceTitle.replace(/:$/, '').trim().toLowerCase();
  if (base.length === 0) {
    return audienceTitle;
  }
  return base.charAt(0).toUpperCase() + base.slice(1);
}

interface FaqAudienceTabProps {
  audience: (typeof MARKETING_FAQ_AUDIENCES)[number];
  index: number;
  isSelected: boolean;
  onSelect: (nextIndex: number) => void;
}

const FaqAudienceTab = ({
  audience,
  index,
  isSelected,
  onSelect,
}: FaqAudienceTabProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <TabTrigger
      onPress={() => {
        onSelect(index);
      }}
      accessibilityRole="tab"
      accessibilityState={{ selected: isSelected }}
      accessibilityLabel={`${formatAudienceAccessibilityLabel(audience.title)} questions`}
      isSelected={isSelected}
      {...webHoverProps}
    >
      <TabTriggerLabel isSelected={isSelected} isHovered={isHovered}>
        {formatAudienceTabDisplay(audience.title)}
      </TabTriggerLabel>
    </TabTrigger>
  );
};

const FaqScreen = () => {
  const { isDesktop } = useBreakpoint();
  const horizontalPad = isDesktop ? 56 : 16;
  const [selectedAudienceIndex, setSelectedAudienceIndex] = useState(0);
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});
  const activeAudience = MARKETING_FAQ_AUDIENCES[selectedAudienceIndex];

  const toggleExpanded = useCallback((id: string) => {
    setExpandedById((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const openSupportEmail = useCallback(() => {
    Linking.openURL(SUPPORT_MAILTO).catch(() => {});
  }, []);

  return (
    <PageScroll isDesktop={isDesktop} contentContainerStyle={{ flexGrow: 1 }}>
      <HeaderBanner
        style={{
          marginHorizontal: -horizontalPad,
          paddingHorizontal: horizontalPad,
        }}
      >
        <BackButton
          containerStyle={{ marginBottom: 16 }}
          fallbackHref={ROUTES.WELCOME.path}
        />
        <Headline>FAQ</Headline>
        <Subheadline>
          We&apos;re here to help. Everything about EMME, answered.
        </Subheadline>
      </HeaderBanner>

      <BodyPanel
        style={{
          marginHorizontal: -horizontalPad,
          paddingHorizontal: horizontalPad,
        }}
      >
        <BodyInner>
          <TabBar accessibilityRole="tablist">
            {MARKETING_FAQ_AUDIENCES.map((audience, index) => {
              const isSelected = selectedAudienceIndex === index;
              return (
                <FaqAudienceTab
                  key={audience.title}
                  audience={audience}
                  index={index}
                  isSelected={isSelected}
                  onSelect={setSelectedAudienceIndex}
                />
              );
            })}
          </TabBar>

          {activeAudience.categories.map((category, categoryIndex) => (
            <View key={`${activeAudience.title}-${category.label}`}>
              <CategoryLabel isFirst={categoryIndex === 0}>
                {category.label}
              </CategoryLabel>
              {category.items.map((item, itemIndex) => {
                const itemId = `faq-${selectedAudienceIndex}-${categoryIndex}-${itemIndex}`;
                const isExpanded = !!expandedById[itemId];
                return (
                  <FaqAccordionRow
                    key={itemId}
                    item={item}
                    itemId={itemId}
                    isExpanded={isExpanded}
                    onToggle={toggleExpanded}
                  />
                );
              })}
            </View>
          ))}

          <ClosingCopy>
            Still have questions? We&apos;re here to help. Email us{' '}
            <ClosingEmailLinkControl
              onPress={openSupportEmail}
              accessibilityRole="link"
              accessibilityLabel={`Email ${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </ClosingEmailLinkControl>
            .
          </ClosingCopy>
        </BodyInner>
      </BodyPanel>
      <FaqScrollSpacer />
      <FaqFooterBleed style={{ marginHorizontal: -horizontalPad }}>
        <Footer />
      </FaqFooterBleed>
    </PageScroll>
  );
};

interface FaqAccordionRowProps {
  item: MarketingFaqItem;
  itemId: string;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}

const FaqAccordionRow = ({
  item,
  itemId,
  isExpanded,
  onToggle,
}: FaqAccordionRowProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <AccordionBlock>
      <QuestionRow
        onPress={() => onToggle(itemId)}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        accessibilityLabel={`${item.question}. ${isExpanded ? 'Expanded' : 'Collapsed'}`}
        {...webHoverProps}
        style={
          IS_WEB && isHovered ? { backgroundColor: alphaBlack05 } : undefined
        }
      >
        <QuestionText>{item.question}</QuestionText>
        <ChevronWrap accessible={false}>
          <Entypo
            name={isExpanded ? 'chevron-thin-down' : 'chevron-thin-right'}
            size={18}
            color={green}
          />
        </ChevronWrap>
      </QuestionRow>
      {isExpanded ? (
        <AnswerBlock>
          <FaqAnswerBody item={item} />
        </AnswerBlock>
      ) : null}
    </AccordionBlock>
  );
};

const FaqApplyHereLink = () => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <AnswerText
      onPress={applyToBeStylist}
      style={[
        answerApplyLinkText,
        IS_WEB && isHovered ? { color: primaryHover } : null,
      ]}
      accessibilityRole="link"
      accessibilityLabel="Apply to be a stylist"
      {...webHoverProps}
    >
      apply here
    </AnswerText>
  );
};

const FaqAnswerBody = ({ item }: { item: MarketingFaqItem }) => {
  if ('answer' in item) {
    return <AnswerText>{item.answer}</AnswerText>;
  }

  return (
    <AnswerText>
      {item.answerSegments.map((segment, index) => {
        if (segment.kind === 'text') {
          return (
            <AnswerText key={index} style={answerTextNested}>
              {segment.text}
            </AnswerText>
          );
        }
        return <FaqApplyHereLink key={index} />;
      })}
    </AnswerText>
  );
};

const ClosingEmailLinkBase = styled(BodyText)({
  ...FONT_STYLES.helvetica,
  fontSize: 15,
  lineHeight: 22,
  fontWeight: '400',
  color: green,
  textDecorationLine: 'underline',
  marginBottom: 0,
});

interface ClosingEmailLinkControlProps {
  onPress: () => void;
  accessibilityRole: 'link';
  accessibilityLabel: string;
  children: React.ReactNode;
}

const ClosingEmailLinkControl = ({
  onPress,
  accessibilityRole,
  accessibilityLabel,
  children,
}: ClosingEmailLinkControlProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <ClosingEmailLinkBase
      onPress={onPress}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      {...webHoverProps}
      style={IS_WEB && isHovered ? { color: primaryHover } : undefined}
    >
      {children}
    </ClosingEmailLinkBase>
  );
};

export default FaqScreen;

const PageScroll = styled(ScrollView)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    width: '100%',
    backgroundColor: green,
    paddingHorizontal: isDesktop ? 56 : 16,
    paddingTop: 0,
  }),
);

const FaqScrollSpacer = styled.View({
  flex: 1,
});

const FaqFooterBleed = styled.View({
  flexGrow: 0,
  flexShrink: 0,
});

const HeaderBanner = styled.View({
  backgroundColor: darkGreen,
  paddingTop: WELCOME_SECTION_VERTICAL_BASE,
  paddingBottom:
    WELCOME_SECTION_VERTICAL_BASE + WELCOME_SECTION_VERTICAL_RHYTHM_EXTRA,
  marginBottom: 0,
});

const Headline = styled(H2)({
  color: sand,
  textAlign: 'center',
  marginBottom: 12,
  width: '100%',
});

const Subheadline = styled(BodyText)({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.helvetica,
  fontSize: 16,
  fontWeight: '300',
  letterSpacing: 0.5,
  lineHeight: 24,
  textAlign: 'center',
  marginBottom: 0,
  maxWidth: 560,
  alignSelf: 'center',
  width: '100%',
});

const BodyPanel = styled.View({
  backgroundColor: sand,
  paddingTop: WELCOME_SECTION_VERTICAL_BASE + 8,
  paddingBottom: WELCOME_SECTION_VERTICAL_BASE,
});

const BodyInner = styled.View({
  width: '100%',
  maxWidth: WELCOME_SECTION_MAX_WIDTH,
  alignSelf: 'center',
});

const TabBar = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  columnGap: 8,
  marginBottom: 4,
});

const TAB_UNDERLINE_WIDTH = 3;

const TabTrigger = styled(Pressable)<{ isSelected: boolean }>(
  ({ isSelected }) => ({
    paddingTop: 4,
    paddingBottom: 5,
    paddingHorizontal: 4,
    marginRight: 16,
    marginBottom: 4,
    borderBottomWidth: TAB_UNDERLINE_WIDTH,
    borderBottomColor: isSelected ? accent : 'transparent',
  }),
);

const TabTriggerLabel = styled(H2)<{
  isSelected: boolean;
  isHovered?: boolean;
}>(({ isSelected, isHovered }) => {
  const labelColor = isSelected || isHovered ? darkGreen : subheadline;
  return {
    ...FONT_STYLES.lora,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: 0.68,
    fontWeight: '400',
    color: labelColor,
    marginBottom: 0,
  };
});

const CategoryLabel = styled(BodyText)<{ isFirst?: boolean }>(
  ({ isFirst }) => ({
    ...FONT_STYLES.helvetica,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    color: subheadline,
    textTransform: 'uppercase',
    marginTop: isFirst ? 16 : 28,
    marginBottom: 4,
  }),
);

const AccordionBlock = styled.View({
  borderBottomWidth: 1,
  borderBottomColor: lightGrey,
});

const QuestionRow = styled(Pressable)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingVertical: 16,
  columnGap: 12,
});

const QuestionText = styled(BodyText)({
  ...FONT_STYLES.helvetica,
  flex: 1,
  fontSize: 18,
  lineHeight: 27,
  fontWeight: '400',
  color: darkGreen,
  marginBottom: 0,
});

const ChevronWrap = styled.View({
  flexShrink: 0,
  paddingTop: 2,
});

const AnswerBlock = styled.View({
  paddingBottom: 16,
  paddingRight: 28,
});

const AnswerText = styled(BodyText)({
  ...FONT_STYLES.helvetica,
  fontSize: 15,
  lineHeight: 22,
  fontWeight: '400',
  color: subheadline,
  marginBottom: 0,
});

const answerTextNested = {
  marginBottom: 0,
};

const answerApplyLinkText = {
  marginBottom: 0,
  ...FONT_STYLES.helvetica,
  fontSize: 15,
  lineHeight: 22,
  fontWeight: '400' as const,
  color: green,
  textDecorationLine: 'underline' as const,
};

const ClosingCopy = styled(BodyText)({
  ...FONT_STYLES.helvetica,
  fontSize: 15,
  lineHeight: 22,
  fontWeight: '400',
  color: subheadline,
  marginBottom: 0,
  marginTop: 40,
});
